import { useEffect, useRef, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { settingsApi } from "@/services/api";

export interface SessionTimeoutOptions {
  timeoutMs?: number;
  warningMs?: number;
  onExpire?: () => void;
}

export interface SessionTimeoutState {
  showWarning: boolean;
  secondsLeft: number;
  keepAlive: () => void;
  forceLogout: () => void;
}

const ACTIVITY_EVENTS = [
  "mousemove", "mousedown", "keydown",
  "touchstart", "scroll", "click", "wheel",
];

export function useSessionTimeout({
  timeoutMs: defaultTimeoutMs = 30 * 60 * 1000,
  warningMs: defaultWarningMs = 2 * 60 * 1000,
  onExpire,
}: SessionTimeoutOptions = {}): SessionTimeoutState {
  const navigate = useNavigate();

  const configRef = useRef({
    timeoutMs: defaultTimeoutMs,
    warningMs: defaultWarningMs,
  });

  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.floor(defaultWarningMs / 1000));

  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const showWarningRef = useRef(false);
  const isDisabledRef = useRef(false);
  const isReadyRef = useRef(false);

  const clearAllTimers = useCallback(() => {
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    logoutTimerRef.current = null;
    warningTimerRef.current = null;
    countdownRef.current = null;
  }, []);

  const doLogout = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    showWarningRef.current = false;
    localStorage.removeItem("erp_user");
    localStorage.removeItem("token");
    if (onExpire) onExpire();
    navigate("/login", { replace: true });
  }, [clearAllTimers, navigate, onExpire]);

  const startCountdown = useCallback(() => {
    const warningSeconds = Math.floor(configRef.current.warningMs / 1000);
    setSecondsLeft(warningSeconds);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) { if (countdownRef.current) clearInterval(countdownRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const resetTimers = useCallback(() => {
    if (isDisabledRef.current || !isReadyRef.current) return;
    if (showWarningRef.current) return;
    clearAllTimers();
    const { timeoutMs, warningMs } = configRef.current;
    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      showWarningRef.current = true;
      startCountdown();
      logoutTimerRef.current = setTimeout(() => doLogout(), warningMs);
    }, timeoutMs - warningMs);
  }, [clearAllTimers, doLogout, startCountdown]);

  const keepAlive = useCallback(() => {
    setShowWarning(false);
    showWarningRef.current = false;
    if (countdownRef.current) clearInterval(countdownRef.current);
    resetTimers();
  }, [resetTimers]);

  const forceLogout = useCallback(() => doLogout(), [doLogout]);

  useEffect(() => {
    // Load timeout config from DB
    settingsApi.getSessionTimeout()
      .then((data: { timeout_minutes?: number; warning_minutes?: number }) => {
        const timeoutMin = data?.timeout_minutes ?? 30;
        const warningMin = data?.warning_minutes ?? 2;
        configRef.current = {
          timeoutMs: timeoutMin > 0 ? timeoutMin * 60 * 1000 : 0,
          warningMs: warningMin * 60 * 1000,
        };
        isDisabledRef.current = timeoutMin === 0;
      })
      .catch(() => {
        // API offline — use defaults
        configRef.current = { timeoutMs: defaultTimeoutMs, warningMs: defaultWarningMs };
        isDisabledRef.current = false;
      })
      .finally(() => {
        isReadyRef.current = true;
        const stored = localStorage.getItem("erp_user");
        if (stored && !isDisabledRef.current) resetTimers();
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for admin changing timeout settings live
  useEffect(() => {
    const handler = (e: Event) => {
      const custom = e as CustomEvent<{ timeoutMin: number; warningMin: number }>;
      const { timeoutMin, warningMin } = custom.detail;
      configRef.current = {
        timeoutMs: timeoutMin > 0 ? timeoutMin * 60 * 1000 : 0,
        warningMs: warningMin * 60 * 1000,
      };
      isDisabledRef.current = timeoutMin === 0;
      clearAllTimers();
      if (!isDisabledRef.current) resetTimers();
    };
    window.addEventListener("erp-session-timeout-updated", handler);
    return () => window.removeEventListener("erp-session-timeout-updated", handler);
  }, [clearAllTimers, resetTimers]);

  useEffect(() => {
    const stored = localStorage.getItem("erp_user");
    if (!stored) return;
    const handleActivity = () => resetTimers();
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, handleActivity, { passive: true }));
    return () => {
      clearAllTimers();
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, handleActivity));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { showWarning, secondsLeft, keepAlive, forceLogout };
}
